import React, { useContext, useEffect, useState, useRef } from 'react';
import Header from '../Header/Header';
import PropTypes from 'prop-types';
import { NiKendoContext } from '../../../context/NiKendoContext';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'types/types';
import Loader from 'components/Common/Loader';
import NotificationCenter from 'components/Layout/NotificationCenter/NotificationCenter';
import { updateApplicationSettings } from 'features/applicationState';
import NIService, { INIService } from 'services/NIService';
import { updateNotificationData } from 'features/notifications';
import AlertComponent from 'components/Common/Alert';
import SideBar from '../SideBar/SideBar';
import { toggleNotification } from 'features/applicationState';
import AppSnackbar from '../NotificationCenter/AppSnackbar';
import UserManager from 'utils/UserManager';
import AIAssistant, {
  Ref as AIAssistantRef,
} from 'components/AIAssistant/AIAssistant';
import { setCustomCss } from 'utils/customCss';

type Props = {
  children: any;
};

export const Workspace = (props: Props) => {
  const { children } = props;
  const dispatch = useDispatch();
  const [isLoadingApi, setIsLoadingApi] = useState(true);

  const aiAssistantRef = useRef<AIAssistantRef>(null);

  const { isLoading }: any = useContext(NiKendoContext);
  const { notificationToggle } = useSelector(
    (state: RootState) => state.applicationState
  );
  // Listen to new notifications coming.
  const notificationCenter = useSelector(
    (state: RootState) => state.notificationCenter
  );
  const handleNotificationToggle = () => {
    dispatch(toggleNotification());
  };

  useEffect(() => {
    const fetchNotificationData = async () => {
      try {
        // Call the API using niService
        const niService: INIService = new NIService();
        const response = await niService.GetNotificationData();

        dispatch(updateNotificationData(response));
      } catch (error) {
        console.error('Error fetching notification data:', error);
      }
    };

    const fetchApplicationSettings = async () => {
      try {
        // Call the API using niService
        const niService: INIService = new NIService();
        const response = await niService.GetApplicationSettings();

        dispatch(updateApplicationSettings(response));
      } catch (error) {
        console.error('Error fetching application settings:', error);
      }
    };
    const fetchUserProperties = async () => {
      try {
        // Call the API using niService
        const niService: INIService = new NIService();
        const response = await niService.GetUserProperties();
        new UserManager(response); 
        const customCssProperty = response.find((prop: any) => prop.Name === "customCss");
        const customCss = customCssProperty?.Value;  
        if (customCss) {
          // Dynamically import the SCSS file based on the customCss value
          try {  
              setCustomCss(customCss);
              await import(`../../../assets/styles/themes/${customCss}-custom-styles.css`); 
          } catch (err) {
            console.error("Error loading custom CSS file:", err);
          }
        }  
      } catch (error) {
        console.error('Error fetching application settings:', error);
      }
    };
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchNotificationData(),
          fetchApplicationSettings(),
          fetchUserProperties(),
        ]);
        setIsLoadingApi(false);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [dispatch]);

  if (isLoading || isLoadingApi) {
    return <Loader />;
  } else
    return (
      <div>
        <Header aiAssistantRef={aiAssistantRef} />
        <div id="sidebar-container" className="sidebar-container">
          <SideBar />
        </div>
        <div
          id="main-content-area-container"
          className="main-content-area-container"
        >
          <div style={{ width: '100%' }}>{children}</div>
        </div>
        <AlertComponent />
        <NotificationCenter
          onClose={handleNotificationToggle}
          open={notificationToggle}
        />
        <AppSnackbar
          show={notificationCenter.newExportSuccess}
          message="New file ready to download"
          severity="success"
        />
        <AppSnackbar
          show={notificationCenter.newExportFail}
          message="Please check Export notification area"
          severity="error"
        />
        <AIAssistant ref={aiAssistantRef} showFabIcon={false} />
      </div>
    );
};

Workspace.propTypes = {
  children: PropTypes.node.isRequired,
};

export default React.memo(Workspace);
